import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Link2,
  Hash,
  User,
  Clock,
  Database,
  FileJson,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import type { AuditLogRecord } from '@/types/compliance';
import {
  actionCategoryLabels,
  sensitivityLabels,
  actionCategoryColors,
  sensitivityColors,
} from '@/types/compliance';

interface AuditTableProps {
  records: AuditLogRecord[];
  isLoading?: boolean;
}

function JsonViewer({ data, label }: { data: Record<string, any> | null; label: string }) {
  if (!data) return <span className="text-muted-foreground italic">Sin datos</span>;

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <pre className="text-xs bg-muted p-2 rounded-md overflow-auto max-h-32 font-mono">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function AuditRow({ record, index }: { record: AuditLogRecord; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const copyHash = () => {
    navigator.clipboard.writeText(record.record_hash);
    setCopied(true);
    toast({ title: 'Hash copiado', description: 'El hash se ha copiado al portapapeles' });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <TableRow className={cn("group", isOpen && "bg-muted/30")}>
        <TableCell>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">
          {record.sequence_number}
        </TableCell>
        <TableCell className="text-xs">
          {format(new Date(record.timestamp_utc), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
        </TableCell>
        <TableCell>
          <span className="font-medium">{record.entity_type}</span>
        </TableCell>
        <TableCell>
          <Badge className={cn("text-xs", actionCategoryColors[record.action_category])}>
            {actionCategoryLabels[record.action_category]}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">{record.performed_by_name}</span>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={cn("text-xs", sensitivityColors[record.sensitivity_level])}>
            {sensitivityLabels[record.sensitivity_level]}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
              {record.record_hash.substring(0, 12)}...
            </code>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={copyHash}
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
        </TableCell>
      </TableRow>

      <CollapsibleContent asChild>
        <TableRow className="bg-muted/20 hover:bg-muted/30">
          <TableCell colSpan={8} className="p-0">
            <div className="p-4 space-y-4">
              {/* Hash Chain Visualization */}
              <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Hash Anterior</p>
                    <code className="text-xs font-mono">{record.previous_hash}</code>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <div className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Hash Actual</p>
                    <code className="text-xs font-mono text-primary">{record.record_hash}</code>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Metadata */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Metadatos
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ID Entidad:</span>
                      <code className="text-xs font-mono">{record.entity_id.substring(0, 8)}...</code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Accion:</span>
                      <span>{record.action}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rol:</span>
                      <span>{record.performed_by_role}</span>
                    </div>
                    {record.ip_address && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">IP:</span>
                        <span>{record.ip_address}</span>
                      </div>
                    )}
                    {record.session_id && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Sesion:</span>
                        <code className="text-xs font-mono">{record.session_id.substring(0, 8)}...</code>
                      </div>
                    )}
                  </div>
                </div>

                {/* Previous State */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <FileJson className="h-4 w-4" />
                    Estado Anterior
                  </h4>
                  <JsonViewer data={record.previous_state} label="" />
                </div>

                {/* New State */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <FileJson className="h-4 w-4" />
                    Estado Nuevo
                  </h4>
                  <JsonViewer data={record.new_state} label="" />
                </div>
              </div>

              {/* Change Summary */}
              {record.change_summary && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm">
                    <span className="font-medium">Resumen:</span> {record.change_summary}
                  </p>
                </div>
              )}

              {/* Compliance Tags */}
              {record.compliance_tags.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Tags:</span>
                  {record.compliance_tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function AuditTable({ records, isLoading }: AuditTableProps) {
  if (isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!records || records.length === 0) {
    return (
      <div className="h-[400px] flex flex-col items-center justify-center text-muted-foreground">
        <Database className="h-12 w-12 mb-2" />
        <p>No hay registros de auditoria</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[600px]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]"></TableHead>
            <TableHead className="w-[80px]">#</TableHead>
            <TableHead className="w-[160px]">Timestamp</TableHead>
            <TableHead>Entidad</TableHead>
            <TableHead className="w-[100px]">Accion</TableHead>
            <TableHead>Usuario</TableHead>
            <TableHead className="w-[100px]">Nivel</TableHead>
            <TableHead className="w-[150px]">Hash</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record, index) => (
            <AuditRow key={record.id} record={record} index={index} />
          ))}
        </TableBody>
      </Table>
    </ScrollArea>
  );
}
